import { Fragment } from 'react'
import { Box, Button, Dialog, Stack, Typography } from '@mui/material'
import useTranslation from '@/hooks/useTranslation'
import {
  impactCounts,
  type PurposeChangeImpact,
} from './decoderPurposeImpact'

/**
 * SCRUM-1032 — a purpose change that would strand another record.
 *
 * The decoder is held by a station, a valve or a pump that needs it to be what
 * it currently is. Changing it is allowed — the user may well be correcting a
 * mistake — but not silently: the record that points at it has to go with it,
 * and that is the user's call, made here rather than discovered later when a
 * picker quietly stops offering the decoder.
 *
 * Two ways out, and both are stated plainly: keep the purpose as it was, or
 * change it and remove what depended on it.
 */

interface PurposeChangeDialogProps {
  open: boolean
  /** `address-index`, so the user can see which decoder this is about. */
  decoderAddress: string
  /**
   * How many decoders the change covers. A fill or paste across several rows
   * is one question about all of them; above one, the dialog speaks of
   * "these decoders" and keeps each its current purpose.
   */
  decoderCount?: number
  fromPurpose: string
  toPurpose: string
  impact: PurposeChangeImpact
  isDeleting?: boolean
  onKeep: () => void
  onProceed: () => void
}

const PurposeChangeDialog = ({
  open,
  decoderAddress,
  decoderCount = 1,
  fromPurpose,
  toPurpose,
  impact,
  isDeleting = false,
  onKeep,
  onProceed,
}: PurposeChangeDialogProps) => {
  const { t } = useTranslation()

  // "2 stations, 1 valve and 1 pump" — each part is a whole phrase of its own,
  // and the joiner is a phrase too, so no sentence is glued out of words here.
  const counts = impactCounts(impact)
  const parts = [
    counts.stations &&
      t(
        counts.stations === 1
          ? 'controllersImpactStationOne'
          : 'controllersImpactStationOther',
        { count: counts.stations },
      ),
    counts.valves &&
      t(
        counts.valves === 1
          ? 'controllersImpactValveOne'
          : 'controllersImpactValveOther',
        { count: counts.valves },
      ),
    counts.pumps &&
      t(
        counts.pumps === 1
          ? 'controllersImpactPumpOne'
          : 'controllersImpactPumpOther',
        { count: counts.pumps },
      ),
  ].filter(Boolean) as string[]

  const summary =
    parts.length <= 1
      ? (parts[0] ?? '')
      : t('controllersImpactListJoin', {
          list: parts.slice(0, -1).join(', '),
          last: parts[parts.length - 1],
        })

  // The sentence is one key. It is split on its own placeholders rather than
  // stitched from fragments, so the translator sees the whole thing and the two
  // purposes still come out bold.
  const isMany = decoderCount > 1
  // A cleared purpose is a change too; the sentence still needs a word for it.
  const toLabel = toPurpose.trim() || '—'
  const bodyParts = t(
    isMany ? 'controllersPurposeChangeBodyMany' : 'controllersPurposeChangeBody',
  ).split(
    /(\{from\}|\{to\}|\{summary\})/,
  )

  const names = [
    ...impact.stations.map((station) => ({
      kind: t('SetupStepStation'),
      label: String(station.description ?? station.id),
    })),
    ...impact.valves.map((valve) => ({
      kind:
        Number(valve.type) === 0 || Number(valve.type) === 3
          ? t('flowValveDevicePump')
          : t('flowValveDeviceValve'),
      label: String(valve.description ?? valve.id),
    })),
  ]

  return (
    <Dialog open={open} onClose={isDeleting ? undefined : onKeep}>
      <Box sx={{ backgroundColor: 'background.default' }}>
        <Box sx={{ padding: '32px 34px 4px 34px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
            }}
          >
            {isMany
              ? t('controllersPurposeChangeTitleMany', { count: decoderCount })
              : t('controllersPurposeChangeTitle', { address: decoderAddress })}
          </Typography>
        </Box>

        <Stack sx={{ padding: '8px 34px 18px 34px', gap: '12px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontSize: '12px',
              lineHeight: 1.4,
              maxWidth: '360px',
            }}
          >
            {bodyParts.map((part, index) => {
              if (part === '{from}')
                return <strong key={index}>{fromPurpose}</strong>
              if (part === '{to}')
                return <strong key={index}>{toLabel}</strong>
              return (
                <Fragment key={index}>
                  {part === '{summary}' ? summary : part}
                </Fragment>
              )
            })}
          </Typography>

          <Stack
            sx={{
              gap: '4px',
              maxWidth: '360px',
              p: '10px 12px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(253,162,155,0.4)' : '#F3C2C5',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(253,162,155,0.08)'
                  : 'rgba(180,35,24,0.05)',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#FDA29B' : '#B42318',
              }}
            >
              {t('controllersPurposeChangeDeletesHeading')}
            </Typography>
            {/* Named, not just counted: "2 stations" is not enough to agree to
                when one of them may be an afternoon's work. */}
            <Stack component="ul" sx={{ m: 0, pl: '16px', gap: '2px' }}>
              {names.slice(0, 8).map((item) => (
                <Box
                  key={`${item.kind}-${item.label}`}
                  component="li"
                  sx={{ fontSize: '12px', lineHeight: 1.35, color: 'text.main' }}
                >
                  {t('controllersPurposeChangeImpactItem', {
                    kind: item.kind,
                    label: item.label,
                  })}
                </Box>
              ))}
              {names.length > 8 && (
                <Box
                  component="li"
                  sx={{ fontSize: '12px', lineHeight: 1.35, color: 'grey.600' }}
                >
                  {t('controllersPurposeChangeMore', {
                    count: names.length - 8,
                  })}
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          sx={{ justifyContent: 'flex-end', pb: '32px', pr: '30px' }}
        >
          <Button
            variant="secondary"
            sx={{ minWidth: '99px' }}
            onClick={onKeep}
            disabled={isDeleting}
          >
            {isMany
              ? t('controllersPurposeChangeKeepMany')
              : t('controllersPurposeChangeKeep', { purpose: fromPurpose })}
          </Button>
          <Button
            variant="primary"
            sx={{ minWidth: '99px' }}
            onClick={onProceed}
            disabled={isDeleting}
          >
            {isDeleting
              ? t('controllersPurposeChangeRemoving')
              : t('controllersPurposeChangeProceed')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default PurposeChangeDialog
